/* ==========================================================================
   Listicle behaviours — one file, data-attribute driven.
   Replaces the per-section inline scripts so each interaction lives once.

   Contracts:
     [data-clg-accordion]        wrapper; items are <details data-clg-acc>
                                 with a [data-clg-acc-panel] inside. One open
                                 at a time, height-animated.
     [data-clg-gallery]          wrapper; [data-clg-slide="i"] images +
                                 [data-clg-thumb="i"] buttons.
     [data-clg-flavours]         wrapper; [data-clg-flavour] buttons (visual
                                 select only).
     [data-clg-scroller]         wrapper with [data-clg-scroll="-1|1"] buttons
                                 and a [data-clg-scroll-track].
     [data-clg-expand]           button; reveals [hidden] items in the
                                 [data-clg-expandable] it points at, then
                                 removes itself.
     [data-clg-video="url"]      click opens the shared video modal.
     [data-clg-lightbox]         wrapper; [data-clg-lb-open] buttons carry
                                 data-clg-lb-src and data-clg-lb-flip.
   ========================================================================== */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

  /* ---- Accordion ---------------------------------------------------- */
  function initAccordion(root) {
    root.querySelectorAll('[data-clg-acc]').forEach(function (item) {
      if (item.dataset.clgReady) return;
      item.dataset.clgReady = '1';
      item.removeAttribute('name'); // JS manages exclusivity so close can animate
      var summary = item.querySelector('summary');
      var panel = item.querySelector('[data-clg-acc-panel]');
      if (!summary || !panel) return;
      var anim = null;
      function done() { anim = null; panel.style.height = ''; }
      summary.addEventListener('click', function (e) {
        e.preventDefault();
        if (anim) anim.cancel();
        if (item.open) {
          if (reduce.matches) { item.open = false; return; }
          var h = panel.offsetHeight;
          anim = panel.animate([{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }],
            { duration: 260, easing: EASE });
          anim.onfinish = function () { item.open = false; done(); };
          anim.oncancel = done;
        } else {
          var box = item.closest('[data-clg-accordion]');
          if (box && !box.hasAttribute('data-clg-acc-multi')) box.querySelectorAll('[data-clg-acc][open]').forEach(function (o) { if (o !== item) o.open = false; });
          item.open = true;
          if (reduce.matches) return;
          var t = panel.offsetHeight;
          anim = panel.animate([{ height: '0px', opacity: 0 }, { height: t + 'px', opacity: 1 }],
            { duration: 260, easing: EASE });
          anim.onfinish = done; anim.oncancel = done;
        }
      });
    });
  }

  /* ---- FAQ accordion (native <details>, class-driven so the CSS animates
     reliably — this theme's global reset breaks [open] attribute matching) -- */
  function initFaqAccordion(root) {
    root.querySelectorAll('.clg-faqacc-item').forEach(function (item) {
      if (item.dataset.clgFaqReady) return;
      item.dataset.clgFaqReady = '1';
      var btn = item.querySelector('.clg-faqacc-q');
      if (!btn) return;
      // The smooth open/close is a pure-CSS grid-template-rows 0fr<->1fr
      // transition on .clg-a-wrap; JS only flips the .is-open class.
      btn.addEventListener('click', function () {
        var open = item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  /* ---- Image gallery ------------------------------------------------ */
  function initGallery(root) {
    root.querySelectorAll('[data-clg-gallery]').forEach(function (g) {
      if (g.dataset.clgReady) return;
      g.dataset.clgReady = '1';
      var slides = g.querySelectorAll('[data-clg-slide]');
      var thumbs = g.querySelectorAll('[data-clg-thumb]');
      thumbs.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var i = btn.getAttribute('data-clg-thumb');
          slides.forEach(function (s) { s.classList.toggle('is-active', s.getAttribute('data-clg-slide') === i); });
          thumbs.forEach(function (t) { t.setAttribute('aria-selected', t === btn ? 'true' : 'false'); });
        });
      });
    });
  }

  /* ---- Flavour picker (visual only) --------------------------------- */
  function initFlavours(root) {
    root.querySelectorAll('[data-clg-flavours]').forEach(function (grp) {
      if (grp.dataset.clgReady) return;
      grp.dataset.clgReady = '1';
      var btns = grp.querySelectorAll('[data-clg-flavour]');
      btns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          btns.forEach(function (b) { b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'); });
        });
      });
    });
  }

  /* ---- Horizontal scroller arrows ----------------------------------- */
  function initScroller(root) {
    root.querySelectorAll('[data-clg-scroller]').forEach(function (sc) {
      if (sc.dataset.clgReady) return;
      sc.dataset.clgReady = '1';
      var track = sc.querySelector('[data-clg-scroll-track]');
      if (!track) return;
      sc.querySelectorAll('[data-clg-scroll]').forEach(function (btn) {
        var dir = parseInt(btn.getAttribute('data-clg-scroll'), 10) || 1;
        btn.addEventListener('click', function () {
          track.scrollBy({ left: dir * Math.round(track.clientWidth * 0.8), behavior: 'smooth' });
        });
      });
    });
  }

  /* ---- Expand (See more) -------------------------------------------- */
  function initExpand(root) {
    root.querySelectorAll('[data-clg-expand]').forEach(function (btn) {
      if (btn.dataset.clgReady) return;
      btn.dataset.clgReady = '1';
      btn.addEventListener('click', function () {
        var target = document.getElementById(btn.getAttribute('data-clg-expand'));
        if (target) target.querySelectorAll('[hidden]').forEach(function (el) { el.removeAttribute('hidden'); });
        if (btn.parentElement) btn.parentElement.remove(); else btn.remove();
      });
    });
  }

  /* ---- Before & After → Real Reviews (2-stage reveal) --------------- */
  /* Mirrors the Lovable Before&After button: click 1 reveals the rest of the
     photos and relabels to "Show More Reviews"; click 2 reveals the hidden
     Real Reviews (UGC) block and scrolls to it; later clicks just scroll.
     [data-clg-ba-more] with optional [data-clg-ba-grid] (photo grid to expand)
     and [data-clg-ba-reviews] (id of the hidden reviews block) + label. */
  function initBaReviews(root) {
    root.querySelectorAll('[data-clg-ba-more]').forEach(function (btn) {
      if (btn.dataset.clgReady) return;
      btn.dataset.clgReady = '1';
      var gridId = btn.getAttribute('data-clg-ba-grid');
      var reviewsId = btn.getAttribute('data-clg-ba-reviews');
      var reviewsLabel = btn.getAttribute('data-clg-ba-reviews-label');
      var labelEl = btn.querySelector('span') || btn;
      btn.addEventListener('click', function () {
        var grid = gridId ? document.getElementById(gridId) : null;
        // Stage 1: reveal the remaining before/after photos.
        if (grid && grid.querySelector('[hidden]')) {
          grid.querySelectorAll('[hidden]').forEach(function (el) { el.removeAttribute('hidden'); });
          if (reviewsId && reviewsLabel) {
            labelEl.textContent = reviewsLabel;           // becomes "Show More Reviews"
          } else if (!reviewsId && btn.parentElement) {
            btn.parentElement.remove();                    // no reviews to gate — done
          }
          return;
        }
        // Stage 2+: reveal the Real Reviews block and scroll to it.
        if (reviewsId) {
          var rv = document.getElementById(reviewsId);
          if (rv) {
            rv.removeAttribute('hidden');
            rv.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      });
    });
  }

  /* ---- Cart: flavour + plan → real variant/selling plan ------------- */
  /* Reads the [data-clg-cart-config] map, and on a [data-clg-addcart] click
     resolves the current flavour + plan to a variant (+ Skio selling plan),
     fills the hidden [data-clg-addform] and triggers the theme's
     [data-add-to-cart] flow (native add + cart drawer). If nothing resolves,
     the CTA keeps its href (PDP) — never a dead button.
     A CTA may carry data-clg-plan="90" to force a plan (e.g. the sticky bar). */
  /* Keeps the header cart badge [data-clg-cart-count] in sync with the real cart.
     The Ultra CTA delegates the add to the theme's own flow, which does NOT touch
     our badge, so we refresh it from /cart.js on load and after any /cart mutation.
     The badge only exists in the green nav, so this no-ops on collagen pages. */
  function refreshCartCount() {
    var els = document.querySelectorAll('[data-clg-cart-count]');
    if (!els.length) return;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { els.forEach(function (el) { el.textContent = c.item_count; }); })
      .catch(function () {});
  }
  function initCartCount() {
    if (!document.querySelector('[data-clg-cart-count]')) return;
    refreshCartCount(); // correct any stale server-rendered value
    if (window.__clgCartHook) return;
    window.__clgCartHook = true;
    var _fetch = window.fetch;
    if (typeof _fetch === 'function') {
      window.fetch = function () {
        var res = _fetch.apply(this, arguments);
        try {
          var a0 = arguments[0];
          var url = (a0 && a0.url) ? a0.url : (typeof a0 === 'string' ? a0 : '');
          if (/\/cart\/(add|change|update|clear)(\.js)?/.test(url)) {
            res.then(function () { setTimeout(refreshCartCount, 150); }, function () {});
          }
        } catch (e) {}
        return res;
      };
    }
    ['cart:updated', 'cart:refresh', 'cart:change'].forEach(function (ev) {
      document.addEventListener(ev, refreshCartCount);
    });
  }

  /* Opens the theme's native cart drawer from the Ultra nav cart icon. The
     drawer's own toggles are snapshotted by <cart-element> at init, but our nav
     link renders after the theme header, so it isn't bound — we open the drawer
     via its element API (fallback: click a theme toggle). No-ops on collagen
     (no .clg-uh-cart). */
  function initCartOpen(root) {
    root.querySelectorAll('.clg-uh-cart').forEach(function (a) {
      if (a.dataset.clgCartReady) return;
      a.dataset.clgCartReady = '1';
      a.addEventListener('click', function (e) {
        var cart = document.querySelector('[data-cart-drawer]');
        if (!cart) return; // no drawer → let the href (cart page) run
        e.preventDefault();
        if (typeof cart.openCartDrawer === 'function') { cart.openCartDrawer(); return; }
        if (typeof cart.toggleCartDrawer === 'function') { cart.toggleCartDrawer(); return; }
        var toggle = document.querySelector('[data-cart-drawer-toggle]');
        if (toggle) toggle.click();
      });
    });
  }

  function initCart(root) {
    var cfgEl = document.querySelector('[data-clg-cart-config]');
    var form = document.querySelector('[data-clg-addform]');
    if (!cfgEl || !form) return;
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    var trigger = form.querySelector('[data-clg-addtrigger]');
    var idInput = form.querySelector('[name="id"]');
    var spInput = form.querySelector('[data-clg-sp]');

    function currentFlavourKey() {
      var b = document.querySelector('[data-clg-flavours] [data-clg-flavour][aria-pressed="true"]')
           || document.querySelector('[data-clg-flavours] [data-clg-flavour]');
      return b ? b.getAttribute('data-clg-flavour-key') : null;
    }
    function currentPlanKey() {
      var c = document.querySelector('[data-clg-plan-group] [data-clg-plan-card].is-selected')
           || document.querySelector('[data-clg-plan-group] [data-clg-plan-card][aria-checked="true"]')
           || document.querySelector('[data-clg-plan-group] [data-clg-plan-card]');
      return c ? c.getAttribute('data-clg-plan-key') : null;
    }

    /* Instant acknowledgment for an add-to-cart tap: the drawer only opens
       after the add POST plus a drawer-render fetch (~1.5-2.5s), and the
       theme's own is-loading class lands on the hidden trigger where nobody
       sees it. The CTA wears .clg-cta-adding (centered spinner, label yields,
       double-clicks blocked) until the drawer actually opens — released by
       the theme's theme:cart:open event, or an 8s failsafe if the add fails. */
    function holdCtaWhileAdding(cta) {
      cta.classList.add('clg-cta-adding');
      cta.setAttribute('aria-busy', 'true');
      function release() {
        cta.classList.remove('clg-cta-adding');
        cta.removeAttribute('aria-busy');
        document.removeEventListener('theme:cart:open', release);
        clearTimeout(failsafe);
      }
      document.addEventListener('theme:cart:open', release);
      var failsafe = setTimeout(release, 8000);
    }

    root.querySelectorAll('[data-clg-addcart]').forEach(function (cta) {
      if (cta.dataset.clgReady) return;
      cta.dataset.clgReady = '1';
      cta.addEventListener('click', function (e) {
        if (cta.classList.contains('clg-cta-adding')) { e.preventDefault(); return; }
        var fk = currentFlavourKey();
        var pk = cta.getAttribute('data-clg-plan') || currentPlanKey();
        var entry = fk && pk && cfg[fk] && cfg[fk][pk];
        if (!entry || !entry.id || !trigger) return; // let the href (PDP) run
        e.preventDefault();
        idInput.value = entry.id;
        if (entry.sp) { spInput.disabled = false; spInput.value = entry.sp; }
        else { spInput.disabled = true; spInput.value = ''; }
        holdCtaWhileAdding(cta);
        trigger.click(); // theme: add to cart + open drawer
        setTimeout(refreshCartCount, 700);
        setTimeout(refreshCartCount, 1800);
      });
    });
  }

  /* ---- Future monthly gifts (collapsible inside a plan card) --------- */
  function initGiftsToggle(root) {
    root.querySelectorAll('[data-clg-gifts-toggle]').forEach(function (btn) {
      if (btn.dataset.clgReady) return;
      btn.dataset.clgReady = '1';
      btn.addEventListener('click', function (e) {
        e.stopPropagation(); // don't also re-trigger plan-card selection
        var wrap = btn.closest('[data-clg-gifts]');
        var panel = wrap && wrap.querySelector('[data-clg-gifts-panel]');
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (panel) { if (open) panel.setAttribute('hidden', ''); else panel.removeAttribute('hidden'); }
      });
    });
  }

  /* ---- Shared video modal ------------------------------------------- */
  var vmodal = null;
  function videoModal() {
    if (vmodal) return vmodal;
    vmodal = document.createElement('div');
    vmodal.className = 'clg-vmodal';
    vmodal.setAttribute('role', 'dialog');
    vmodal.setAttribute('aria-modal', 'true');
    vmodal.innerHTML =
      '<button type="button" class="clg-vmodal-close" aria-label="Close">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg></button>' +
      '<video controls playsinline preload="none"></video>';
    document.body.appendChild(vmodal);
    var vid = vmodal.querySelector('video');
    function close() {
      vmodal.classList.remove('is-open');
      vid.pause(); vid.removeAttribute('src'); vid.load();
      document.body.style.overflow = '';
    }
    vmodal.querySelector('.clg-vmodal-close').addEventListener('click', close);
    vmodal.addEventListener('click', function (e) { if (e.target === vmodal) close(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && vmodal.classList.contains('is-open')) close(); });
    vmodal._open = function (src) {
      vid.src = src; vmodal.classList.add('is-open'); document.body.style.overflow = 'hidden';
      var p = vid.play(); if (p && p.catch) p.catch(function () {});
    };
    return vmodal;
  }
  function initVideoTriggers(root) {
    root.querySelectorAll('[data-clg-video]').forEach(function (el) {
      if (el.dataset.clgReady) return;
      el.dataset.clgReady = '1';
      el.addEventListener('click', function () {
        var src = el.getAttribute('data-clg-video');
        if (src) videoModal()._open(src);
      });
    });
  }

  /* ---- Shared lightbox ---------------------------------------------- */
  var lb = null;
  function buildLightbox() {
    lb = document.createElement('div');
    lb.className = 'clg-lb';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.innerHTML =
      '<button type="button" class="clg-lb-btn clg-lb-close" aria-label="Close"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg></button>' +
      '<button type="button" class="clg-lb-btn clg-lb-prev" aria-label="Previous"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="m15 18-6-6 6-6"/></svg></button>' +
      '<img alt="">' +
      '<button type="button" class="clg-lb-btn clg-lb-next" aria-label="Next"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="m9 18 6-6-6-6"/></svg></button>';
    document.body.appendChild(lb);
    lb._img = lb.querySelector('img');
    lb._items = [];
    lb._i = 0;
    function show(i) {
      lb._i = (i + lb._items.length) % lb._items.length;
      var it = lb._items[lb._i];
      lb._img.src = it.src;
      lb._img.classList.toggle('is-flipped', it.flip);
    }
    function close() { lb.classList.remove('is-open'); document.body.style.overflow = ''; }
    lb._show = show;
    lb._openWith = function (items, i) { lb._items = items; lb.classList.add('is-open'); document.body.style.overflow = 'hidden'; show(i); };
    lb.querySelector('.clg-lb-close').addEventListener('click', close);
    lb.querySelector('.clg-lb-prev').addEventListener('click', function () { show(lb._i - 1); });
    lb.querySelector('.clg-lb-next').addEventListener('click', function () { show(lb._i + 1); });
    lb.addEventListener('click', function (e) { if (e.target === lb) close(); });
    document.addEventListener('keydown', function (e) {
      if (!lb.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') show(lb._i - 1);
      else if (e.key === 'ArrowRight') show(lb._i + 1);
    });
    return lb;
  }
  function initLightbox(root) {
    root.querySelectorAll('[data-clg-lightbox]').forEach(function (grp) {
      if (grp.dataset.clgReady) return;
      grp.dataset.clgReady = '1';
      var opens = Array.prototype.slice.call(grp.querySelectorAll('[data-clg-lb-open]'));
      var items = opens.map(function (o) {
        return { src: o.getAttribute('data-clg-lb-src'), flip: o.getAttribute('data-clg-lb-flip') === 'true' };
      });
      opens.forEach(function (o, i) {
        o.addEventListener('click', function () { (lb || buildLightbox())._openWith(items, i); });
      });
    });
  }

  /* ---- Rolling countdown ------------------------------------------- */
  function initCountdown(root) {
    root.querySelectorAll('[data-clg-countdown]').forEach(function (el) {
      if (el.dataset.clgReady) return;
      el.dataset.clgReady = '1';
      var hours = parseFloat(el.getAttribute('data-clg-countdown')) || 24;
      var WINDOW = hours * 3600 * 1000;
      var KEY = 'clg_cd_' + (el.getAttribute('data-clg-cd-key') || 'default');
      function deadline() {
        try {
          var raw = window.localStorage.getItem(KEY);
          var n = raw ? parseInt(raw, 10) : 0;
          if (n && n > Date.now()) return n;
        } catch (e) {}
        var next = Date.now() + WINDOW;
        try { window.localStorage.setItem(KEY, String(next)); } catch (e) {}
        return next;
      }
      var cells = {
        d: el.querySelector('[data-cd="d"]'), h: el.querySelector('[data-cd="h"]'),
        m: el.querySelector('[data-cd="m"]'), s: el.querySelector('[data-cd="s"]')
      };
      function pad(n) { return String(n).length < 2 ? '0' + n : '' + n; }
      function tick() {
        var diff = Math.max(0, deadline() - Date.now());
        if (cells.d) cells.d.textContent = pad(Math.floor(diff / 86400000));
        if (cells.h) cells.h.textContent = pad(Math.floor((diff / 3600000) % 24));
        if (cells.m) cells.m.textContent = pad(Math.floor((diff / 60000) % 60));
        if (cells.s) cells.s.textContent = pad(Math.floor((diff / 1000) % 60));
      }
      tick();
      setInterval(tick, 1000);
    });
  }

  /* ---- Init ---------------------------------------------------------- */
  function init(root) {
    root = root || document;
    initAccordion(root);
    initFaqAccordion(root);
    initGallery(root);
    initFlavours(root);
    initScroller(root);
    initExpand(root);
    initBaReviews(root);
    initCart(root);
    initCartCount();
    initCartOpen(root);
    initGiftsToggle(root);
    initVideoTriggers(root);
    initLightbox(root);
    initCountdown(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  // Re-init sections added/edited in the theme editor.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
